<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.editLocation')"
        no-reset
        size="large"
        :rules="rules"
        :submit="handleSubmit"
        @close="closeOverSide">
        <hts-card :title="$t('labels.location')">
            <p class="font-bold text-warning-500">
                Chỉnh sửa địa điểm để cập nhật địa chỉ chi tiết trong phạm vi gần điểm hiện tại, không thay đổi sang địa điểm khác hoàn toàn.
            </p>
            <p class="font-bold text-warning-500">
                Những thông tin đã được tạo với tên địa điểm trước khi thay đổi có thể sẽ không được cập nhật.
            </p>
            <div class="grid gap-4 grid-cols-1 sm:grid-cols-2">
                <hts-form-input
                    v-model="formData.address"
                    :label="$t('labels.address')"
                    :color="color"
                    name="address"
                    :disabled="true"
                    prop="address" />
                <hts-form-input
                    v-model="formData.locationName"
                    :label="$t('labels.locationName')"
                    :color="color"
                    name="locationName"
                    prop="locationName" />
                <hts-form-input
                    v-model="formData.latitudeAndLongitude"
                    :label="$t('labels.latitudeAndLongitude')"
                    :color="color"
                    :disabled="true"
                    name="latitudeAndLongitude"
                    prop="latitudeAndLongitude" />
            </div>
        </hts-card>

        <hts-card :title="$t('labels.position')">
            <hts-map-autocomplete
                v-model="findPosition"
                :label="$t('labels.findPosition')"
                :placeholder="$t('labels.nodeModule.search')"
                @get-place-detail="(data) => handleSetPosition(data)" />
            <hts-map-node
                class="min-h-[600px]"
                :center-position="dataPosition"
                center-marker
                :node-list="locationList"
                @click="getLocationMap" />
        </hts-card>
    </hts-drawer>
</template>
<script setup lang="ts">
const props = defineProps({
  color: {
    type: String
  }
})
const emits = defineEmits(['close', 'refreshTables', 'newLocation'])

const visible = ref(false)

const rules = {
  address: 'required',
  locationName: 'required',
  latitudeAndLongitude: 'required'
}

const dataPosition = ref<{ lat: number; lng: number; placeId?: string }>({
  lat: 0,
  lng: 0
})

const locationList = ref<Array<{ [key: string]: any }>>([])
const findPosition = ref('')
const locationId = ref('')

const formData = ref<{ [key: string]: any }>({
  address: '',
  locationName: '',
  placeId: '',
  latitudeAndLongitude: '',
  lat: '',
  lng: '',
  newLocation: false
} as { [key: string]: any })

const open = (id: string) => {
  locationId.value = id
  visible.value = true
  getData()
  getAllNode()
}

const closeOverSide = () => {
  visible.value = false
}

const geocodeLocation = async (lat: number, lng: number) => {
  const geocoder = new google.maps.Geocoder()
  const { results } = await geocoder.geocode({ location: { lat, lng } })

  return results
}

const updateLocationData = async (data: any) => {
  const lat = data.geometry.location.lat()
  const lng = data.geometry.location.lng()
  const addressDetail = await geocodeLocation(lat, lng)

  formData.value = {
    address: data.formatted_address,
    addressDetail,
    locationName: data.name,
    placeId: data.place_id,
    latitudeAndLongitude: `${lat}, ${lng}`,
    lat,
    lng,
    newLocation:
      Array.isArray(data.address_components?.[0]?.types) &&
      (data.address_components?.[0]?.types.includes('street_address') || data.address_components?.[0]?.types.includes('premise'))
  }

  dataPosition.value = { lat, lng }
  findPosition.value = data.name
}

const handleSetPosition = async (data: any) => {
  if (data) { await updateLocationData(data) }
}

const getLocationMap = async (location: any, place: any) => {
  if (place) {
    await updateLocationData(place)
  } else if (location) {
    const { lat, lng } = location
    const results = await geocodeLocation(lat, lng)

    formData.value = {
      address: results[0]?.formatted_address || '',
      locationName: '',
      placeId: results[0]?.place_id || '',
      addressDetail: results,
      latitudeAndLongitude: `${lat}, ${lng}`,
      lat,
      lng,
      newLocation: true
    }

    dataPosition.value = { lat, lng }
    findPosition.value = results[0]?.formatted_address || ''
  }
}

const getData = async () => {
  const api = $urlAdmin.node.location.detail
  const data: any = locationId.value ? await $api(api, { uriParams: { ID: locationId.value } }) : null
  const resData = data?.data?.value
  if (resData.success) {
    formData.value.address = resData.data.address
    formData.value.locationName = resData.data.locationName
    formData.value.placeId = resData.data.placeId
    formData.value.latitudeAndLongitude = `${resData.data.lat}, ${resData.data.lng}`
    formData.value.lat = resData.data.lat
    formData.value.lng = resData.data.lng
    formData.value.newLocation = resData.data.newLocation

    dataPosition.value = {
      lat: parseFloat(resData.data.lat),
      lng: parseFloat(resData.data.lng)
    }
  }
}

const getAllNode = async () => {
  const api = $urlAdmin.node.location.getAll
  const data: any = await $api(api)
  const resData = data.data.value

  if (resData?.success) {
    const list = resData.data
    locationList.value = list.map((item: any) => ({
      position: {
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lng)
      },
      isNewLocation: item.newLocation,
      icon: item.newLocation ? '/assets/new-location.png' : '/assets/location.png',
      title: item.locationName,
      placeId: item.placeId
    }))
  }
}

defineExpose({
  open
})

const handleSubmit = async () => {
  const api = $urlAdmin.node.location.edit
  const data: any = await $api(api, { body: formData.value, uriParams: { ID: locationId.value } })
  const resData = data?.data?.value
  if (resData.success) {
    process.client && $toast().success($t('labels.editLocationSuccess'))
    resetFields()
    closeOverSide()
    emits('newLocation', resData.data)
    emits('refreshTables')
  }
}

const resetFields = () => {
  formData.value = {
    address: '',
    locationName: '',
    placeId: '',
    latitudeAndLongitude: '',
    lat: 0,
    lng: 0,
    newLocation: false
  }

  dataPosition.value = {
    lat: 10.7561542,
    lng: 106.6896784,
    placeId: ''
  }
  findPosition.value = ''
}
</script>
